import React, { useState } from "react";
import { AlertCircle, CheckCircle, X } from "lucide-react";
import { apiCall } from "../api";
import { useMountEffect } from "../hooks/useMountEffect";
import "./AdminPages.css";

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [toast, setToast] = useState({ show: false, type: "", message: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState(null);

  const isSuperAdmin = currentUserRole === "superadmin";

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: "", message: "" }), 4000);
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await apiCall("/api/settings");
      setSettings(data);
    } catch (err) {
      console.error("Error fetching settings:", err);
      setError("Failed to load settings. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  useMountEffect(() => {
    // Get current user role and fetch settings only if authenticated
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");

    if (!token || !userData) {
      setError("Please login to access this page");
      setLoading(false);
      return;
    }

    const user = JSON.parse(userData);
    setCurrentUserRole(user.role);
    fetchSettings();
  });

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    const updatedSettings = {
      ...settings,
      [name]: type === "checkbox" ? checked : value,
    };

    setSettings(updatedSettings);

    // Auto-save the setting
    try {
      await apiCall("/api/settings", {
        method: "POST",
        body: JSON.stringify(updatedSettings),
      });
      showToast("success", `${name} updated successfully`);
    } catch (err) {
      showToast("error", "Failed to save setting");
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>⚙️ System Settings</h1>
          <p>Configure system behavior and preferences</p>
        </div>
        <p>Loading settings...</p>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>⚙️ System Settings</h1>
          <p>Configure system behavior and preferences</p>
        </div>
        <div className="status-message error">
          <AlertCircle size={16} /> Failed to load settings
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '32px 20px' }}>
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          {toast.type === "success" ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span>{toast.message}</span>
          <button
            className="toast-close"
            onClick={() => setToast({ show: false, type: "", message: "" })}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>⚙️ System Settings</h1>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#6b7280' }}>Configure system behavior and preferences</p>
          </div>
          {!isSuperAdmin && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 16px",
                background: "#fef3c7",
                borderRadius: "8px",
                color: "#92400e",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              🔒 Read Only Mode
            </div>
          )}
        </div>

        {error && (
          <div className="status-message error">
            <AlertCircle size={16} /> {error}
          </div>
        )}

      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>🤖 AI Extraction Provider</h3>
        <div className="settings-group">
          <div className="form-group">
            <label htmlFor="aiProvider">AI Provider for OCR Extraction</label>
            <select
              id="aiProvider"
              name="aiProvider"
              value={settings.aiProvider || "openrouter"}
              onChange={handleChange}
              disabled={!isSuperAdmin}
            >
              <option value="gemini">Google Gemini (gemma-4-31b-it)</option>
              <option value="openrouter">OpenRouter (gemma-4-31b-it)</option>
              <option value="bedrock">AWS Bedrock - Qwen3-32B (text-only, ₹0.12/form)</option>
              <option value="bedrock-vl">AWS Bedrock - Qwen3-VL-235B (vision, ₹0.30/form)</option>
            </select>
            <small>
              Select which AI provider to use for document extraction. Bedrock
              uses instance role (no API key needed).
            </small>
          </div>

          <div className="form-group">
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Enable GLIB Parallel Extraction
                </label>
                <small style={{ color: "#6b7280" }}>
                  Run GLIB extraction in parallel with main extraction for
                  comparison
                </small>
              </div>
              <label
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: isSuperAdmin ? "pointer" : "not-allowed",
                  opacity: isSuperAdmin ? 1 : 0.5,
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  id="enableGLIB"
                  name="enableGLIB"
                  checked={settings.enableGLIB !== false}
                  onChange={handleChange}
                  disabled={!isSuperAdmin}
                  style={{ display: "none" }}
                />
                <div
                  style={{
                    width: "48px",
                    height: "28px",
                    backgroundColor:
                      settings.enableGLIB !== false ? "#10b981" : "#ef4444",
                    borderRadius: "14px",
                    transition: "background-color 0.3s",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      backgroundColor: "white",
                      borderRadius: "12px",
                      position: "absolute",
                      top: "2px",
                      left: settings.enableGLIB !== false ? "22px" : "2px",
                      transition: "left 0.3s",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>📁 File Processing</h3>
        <div className="settings-group">
          <div className="form-group">
            <label htmlFor="maxFileSize">Maximum File Size (MB)</label>
            <input
              type="number"
              id="maxFileSize"
              name="maxFileSize"
              value={settings.maxFileSize}
              onChange={handleChange}
              min="1"
              max="500"
              disabled={!isSuperAdmin}
            />
            <small>Maximum size for individual file uploads</small>
          </div>

          <div className="form-group">
            <label htmlFor="processingTimeout">
              Processing Timeout (seconds)
            </label>
            <input
              type="number"
              id="processingTimeout"
              name="processingTimeout"
              value={settings.processingTimeout}
              onChange={handleChange}
              min="30"
              max="3600"
              disabled={!isSuperAdmin}
            />
            <small>Maximum time to process a single work order</small>
          </div>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>🔔 Notifications & Retries</h3>
        <div
          className="settings-group"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "24px",
            alignItems: "start",
          }}
        >
          <div className="form-group">
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Enable Email Notifications
                </label>
                <small style={{ color: "#6b7280" }}>
                  Send notifications for work order status changes
                </small>
              </div>
              <label
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: isSuperAdmin ? "pointer" : "not-allowed",
                  opacity: isSuperAdmin ? 1 : 0.5,
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  id="enableNotifications"
                  name="enableNotifications"
                  checked={settings.enableNotifications}
                  onChange={handleChange}
                  disabled={!isSuperAdmin}
                  style={{ display: "none" }}
                />
                <div
                  style={{
                    width: "48px",
                    height: "28px",
                    backgroundColor: settings.enableNotifications
                      ? "#10b981"
                      : "#ef4444",
                    borderRadius: "14px",
                    transition: "background-color 0.3s",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      backgroundColor: "white",
                      borderRadius: "12px",
                      position: "absolute",
                      top: "2px",
                      left: settings.enableNotifications ? "22px" : "2px",
                      transition: "left 0.3s",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>
              </label>
            </div>
          </div>

          <div className="form-group">
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Enable Auto-Retry
                </label>
                <small style={{ color: "#6b7280" }}>
                  Automatically retry failed work orders
                </small>
              </div>
              <label
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: isSuperAdmin ? "pointer" : "not-allowed",
                  opacity: isSuperAdmin ? 1 : 0.5,
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  id="autoRetry"
                  name="autoRetry"
                  checked={settings.autoRetry}
                  onChange={handleChange}
                  disabled={!isSuperAdmin}
                  style={{ display: "none" }}
                />
                <div
                  style={{
                    width: "48px",
                    height: "28px",
                    backgroundColor: settings.autoRetry ? "#10b981" : "#ef4444",
                    borderRadius: "14px",
                    transition: "background-color 0.3s",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      backgroundColor: "white",
                      borderRadius: "12px",
                      position: "absolute",
                      top: "2px",
                      left: settings.autoRetry ? "22px" : "2px",
                      transition: "left 0.3s",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>
              </label>
            </div>
          </div>

          {settings.autoRetry && (
            <div className="form-group">
              <label htmlFor="retryAttempts">Retry Attempts</label>
              <input
                type="number"
                id="retryAttempts"
                name="retryAttempts"
                value={settings.retryAttempts}
                onChange={handleChange}
                min="1"
                max="10"
                disabled={!isSuperAdmin}
              />
              <small>Number of times to retry failed operations</small>
            </div>
          )}
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>💾 Data Management</h3>
        <div
          className="settings-group"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "24px",
            alignItems: "start",
          }}
        >
          <div className="form-group">
            <label htmlFor="dataRetentionDays">Data Retention (days)</label>
            <input
              type="number"
              id="dataRetentionDays"
              name="dataRetentionDays"
              value={settings.dataRetentionDays}
              onChange={handleChange}
              min="30"
              max="730"
              disabled={!isSuperAdmin}
            />
            <small>
              Automatically delete work orders older than this period
            </small>
          </div>

          <div className="form-group">
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Enable Audit Logging
                </label>
                <small style={{ color: "#6b7280" }}>
                  Log all system operations for compliance
                </small>
              </div>
              <label
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: isSuperAdmin ? "pointer" : "not-allowed",
                  opacity: isSuperAdmin ? 1 : 0.5,
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  id="enableAuditLog"
                  name="enableAuditLog"
                  checked={settings.enableAuditLog}
                  onChange={handleChange}
                  disabled={!isSuperAdmin}
                  style={{ display: "none" }}
                />
                <div
                  style={{
                    width: "48px",
                    height: "28px",
                    backgroundColor: settings.enableAuditLog
                      ? "#10b981"
                      : "#ef4444",
                    borderRadius: "14px",
                    transition: "background-color 0.3s",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      backgroundColor: "white",
                      borderRadius: "12px",
                      position: "absolute",
                      top: "2px",
                      left: settings.enableAuditLog ? "22px" : "2px",
                      transition: "left 0.3s",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>💾 Backup & Recovery</h3>
        <div
          className="settings-group"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "24px",
            alignItems: "start",
          }}
        >
          <div className="form-group">
            <label htmlFor="backupFrequency">Backup Frequency</label>
            <select
              id="backupFrequency"
              name="backupFrequency"
              value={settings.backupFrequency}
              onChange={handleChange}
              disabled={!isSuperAdmin || !settings.enableBackups}
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <small>How often to create backups</small>
          </div>
          <div className="form-group">
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Enable Automated Backups
                </label>
                <small style={{ color: "#6b7280" }}>
                  Automatically backup system data
                </small>
              </div>
              <label
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: isSuperAdmin ? "pointer" : "not-allowed",
                  opacity: isSuperAdmin ? 1 : 0.5,
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  id="enableBackups"
                  name="enableBackups"
                  checked={settings.enableBackups}
                  onChange={handleChange}
                  disabled={!isSuperAdmin}
                  style={{ display: "none" }}
                />
                <div
                  style={{
                    width: "48px",
                    height: "28px",
                    backgroundColor: settings.enableBackups
                      ? "#10b981"
                      : "#ef4444",
                    borderRadius: "14px",
                    transition: "background-color 0.3s",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      backgroundColor: "white",
                      borderRadius: "12px",
                      position: "absolute",
                      top: "2px",
                      left: settings.enableBackups ? "22px" : "2px",
                      transition: "left 0.3s",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Settings;
